import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import { isSnapMagicEnabled, openSnapMagicModal } from '../../../utils/snapmagic';

const useStyles = makeStyles({
	link: {
		display: 'inline-flex',
		alignItems: 'center',
		color: '#0076A8',
		textDecoration: 'none',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		'&:hover': {
			textDecoration: 'underline'
		},
		'&:focus-visible': {
			outline: '2px solid #333',
			outlineOffset: 2
		},
		'&[aria-disabled="true"]': {
			color: '#666',
			cursor: 'progress'
		}
	},
	progress: {
		display: 'inline-flex',
		width: 12,
		height: 12,
		marginLeft: 6
	}
});

function SnapMagicViewer({ partNumber, lang = 'en' }) {
	const classes = useStyles();
	const [enabled, setEnabled] = useState(false);
	const [status, setStatus] = useState('idle');
	const attemptRef = useRef(null);

	useEffect(() => {
		let active = true;
		if (partNumber) isSnapMagicEnabled().then(value => active && setEnabled(value));
		return () => {
			active = false;
		};
	}, [partNumber]);

	useEffect(() => {
		const attempt = { active: true, request: 0 };
		attemptRef.current = attempt;
		setStatus('idle');
		return () => {
			attempt.active = false;
		};
	}, [partNumber, lang]);

	if (!partNumber || !enabled) return null;
	const openViewer = async event => {
		event.preventDefault();
		if (status === 'loading') return;
		const attempt = attemptRef.current;
		const request = ++attempt.request;
		const onSettled = () => {
			if (attempt.active && request === attempt.request) setStatus('idle');
		};
		setStatus('loading');
		try {
			await openSnapMagicModal({ part: partNumber, lang, onSettled });
		} catch (_error) {
			onSettled();
		}
	};
	return (
		<a
			href="#"
			className={classes.link}
			aria-label="View CAD"
			aria-busy={status === 'loading'}
			data-status={status}
			title={status === 'loading' ? 'Loading CAD models...' : 'CAD models'}
			aria-disabled={status === 'loading'}
			onClick={openViewer}>
			View CAD
			<span className={classes.progress}>
				{status === 'loading' && <CircularProgress size={12} color="inherit" aria-label="Loading CAD models" />}
			</span>
		</a>
	);
}

SnapMagicViewer.propTypes = {
	partNumber: PropTypes.string,
	lang: PropTypes.string
};

export default SnapMagicViewer;
